import { setCookie } from './setCookie'
import { CookieEnum } from '../../enums/cookies'
import { getCookieByName } from './getCookieByName'
import { deleteCookie } from './deleteCookie'
import { listenForCrossTabLogout, isLoggedOut } from './crossTabLogout'
import { CookieOptions } from './cookieTypes'

const domainCookie = '.kolo-app.com'

/**
 * Get company id cookie
 * @returns
 */
const getCompanyIdCookie = () => {
  const companyCookieName = CookieEnum.company
  const companyId = getCookieByName(companyCookieName)
  return companyId
}

/**
 * Delete all session cookies
 */
const deleteAllSessionCookies = () => {
  const cookiesToDelete = [
    CookieEnum.refreshToken,
    CookieEnum.sessionToken,
    CookieEnum.koloToken,
    CookieEnum.userTokenId,
    CookieEnum.userTokenCustom,
    CookieEnum.userTokenRefresh,
    CookieEnum.userTokenSignature,
    CookieEnum.currentUser,
    CookieEnum.company,
    CookieEnum.authToken,
  ]

  // Delete each cookie with multiple combinations to ensure deletion
  cookiesToDelete.forEach(cookieName => {
    // Try to delete with current cookie properties first
    const currentProperties = getCurrentCookieProperties(cookieName)
    if (currentProperties) {
      deleteCookie(cookieName, currentProperties)
    } else {
      // Fallback to default options
      const defaultOptions: CookieOptions = {
        path: '/',
        domain: domainCookie,
        sameSite: 'Lax',
        secure: process.env.REACT_APP_USE_SECURE_COOKIES === 'true',
      }
      deleteCookie(cookieName, defaultOptions)
    }
  })

  // Clear localStorage as well for cross-tab communication
  if (typeof window !== 'undefined' && window.localStorage) {
    // Signal other tabs about the logout
    localStorage.setItem('logoutEvent', Date.now().toString())
  }
}

/**
 * Get current cookie properties by parsing the document.cookie string
 * @param cookieName
 * @returns CookieOptions or null if cookie doesn't exist
 */
const getCurrentCookieProperties = (cookieName: string): CookieOptions | null => {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  const cookie = cookies.find(c => c.trim().startsWith(`${cookieName}=`))

  if (!cookie) return null

  // Extract properties from the cookie string
  const cookieParts = cookie.split(';')
  const properties: CookieOptions = {
    sameSite: 'Lax', // default
    domain: domainCookie,
    path: '/',
    maxAge: 12 * 60 * 60 * 24,
    httpOnly: false,
  }

  cookieParts.forEach(part => {
    const trimmedPart = part.trim().toLowerCase()
    if (trimmedPart.startsWith('domain=')) {
      properties.domain = trimmedPart.replace('domain=', '')
    } else if (trimmedPart.startsWith('path=')) {
      properties.path = trimmedPart.replace('path=', '')
    } else if (trimmedPart === 'secure') {
      properties.secure = true
    } else if (trimmedPart.startsWith('samesite=')) {
      const sameSiteValue = trimmedPart.replace('samesite=', '')
      properties.sameSite = sameSiteValue.charAt(0).toUpperCase() + sameSiteValue.slice(1)
    } else if (trimmedPart === 'httponly') {
      properties.httpOnly = true
    }
  })

  return properties
}

/**
 * Update company cookie value while preserving existing properties
 * @param company
 */
const setCompanyIdCookie = async (companyId: string) => {
  // Get current cookie properties
  const currentProperties = getCurrentCookieProperties(CookieEnum.company)

  // Use current properties if available, otherwise use defaults
  const cookieOptions: CookieOptions = currentProperties || {
    sameSite: '',
    domain: domainCookie,
    secure: false,
  }

  setCookie(CookieEnum.company, companyId, cookieOptions)
}

export {
  setCompanyIdCookie,
  deleteAllSessionCookies,
  getCompanyIdCookie,
  listenForCrossTabLogout,
  isLoggedOut,
}
